import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Button,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { getDatabase, ref, onValue, off, update } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";

const FeaturedBoats = () => {
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [selectedBoats, setSelectedBoats] = useState({});
  const navigate = useNavigate();

  // Fetch all boats from the database
  useEffect(() => {
    const database = getDatabase();
    const listingsRef = ref(database, "listings");

    const handleData = (snapshot) => {
      if (snapshot.exists()) {
        const boatsData = [];
        snapshot.forEach((childSnapshot) => {
          const boatId = childSnapshot.key;
          const boatData = childSnapshot.val();
          boatsData.push({
            id: boatId,
            ...boatData,
            featured: boatData?.featured || false,
          });
        });
        setBoats(boatsData);

        // Initialize selectedBoats state with current featured status
        const initialSelectedState = {};
        boatsData.forEach((boat) => {
          initialSelectedState[boat.id] = boat?.featured || false;
        });
        setSelectedBoats(initialSelectedState);
      } else {
        setBoats([]);
      }
      setLoading(false);
    };

    onValue(listingsRef, handleData);

    // Clean up the listener when the component unmounts
    return () => {
      off(listingsRef, "value", handleData);
    };
  }, []);

  // Handle checkbox change
  const handleCheckboxChange = (boatId) => {
    setSelectedBoats((prevState) => ({
      ...prevState,
      [boatId]: !prevState[boatId],
    }));
  };

  // Update featured status for selected boats
  const handleUpdateFeatured = async () => {
    try {
      // Set loading and button clicked state immediately to provide visual feedback
      setLoading(true);
      setButtonClicked(true);

      const database = getDatabase();
      const updates = {};
      let changesCount = 0;

      // Prepare all updates
      Object.keys(selectedBoats).forEach((boatId) => {
        // Only update if the boat's featured status has changed
        const boat = boats.find((b) => b.id === boatId);
        if (boat && boat?.featured !== selectedBoats[boatId]) {
          updates[`listings/${boatId}/featured`] = selectedBoats[boatId];
          changesCount++;
        }
      });

      // Apply all updates in a single operation
      if (changesCount > 0) {
        await update(ref(database), updates);
      }

      // Update local boat data to match the new featured status
      const updatedBoats = boats.map((boat) => ({
        ...boat,
        featured: selectedBoats[boat.id] || false,
      }));
      setBoats(updatedBoats);

      // Show success message
      setSuccess(
        `Featured boats updated successfully! ${changesCount} boat(s) updated.`
      );
      setShowToast(true);

      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (error) {
      console.error("Error updating featured boats:", error);
      setError("Failed to update featured boats. Please try again.");
      setShowToast(true);
      setTimeout(() => {
        setError("");
      }, 3000);
    } finally {
      setLoading(false);
      // Reset button clicked state after a delay to maintain the visual feedback
      setTimeout(() => {
        setButtonClicked(false);
      }, 500);
    }
  };

  

  return (
    <Container className="py-4">
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ zIndex: 1050 }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg={error ? "danger" : "success"}
        >
          <Toast.Header closeButton>
            <strong className="me-auto">{error ? "Error" : "Success"}</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{error || success}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2>Featured Boats Management</h2>
           
          </div>
          <p className="text-muted">
            Select the boats you want to feature on the homepage. Featured boats
            will be displayed prominently to users.
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col className="d-flex justify-content-end">
          <Button
            variant={buttonClicked ? "success" : "primary"}
            onClick={handleUpdateFeatured}
            disabled={loading}
            className={buttonClicked ? "shadow-sm" : ""}
            style={{
              transform: buttonClicked ? "scale(0.98)" : "scale(1)",
              transition: "all 0.1s ease-in-out",
            }}
          >
            {loading ? (
              <ThreeDots
                visible={true}
                height="20"
                width="40"
                color="#ffffff"
                radius="9"
                ariaLabel="three-dots-loading"
              />
            ) : (
              "Update Featured Boats"
            )}
          </Button>
        </Col>
      </Row>

      {loading && !boats.length ? (
        <div className="text-center py-5">
          <ThreeDots
            visible={true}
            height="50"
            width="50"
            color="#0d6efd"
            radius="9"
            ariaLabel="three-dots-loading"
          />
        </div>
      ) : boats.length === 0 ? (
        <div className="text-center py-5">
          <p>No boats found in the database.</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Featured</th>
                  <th>Image</th>
                  <th>Boat Name</th>
                  <th>Type</th>
                  <th>Capacity</th>
                </tr>
              </thead>
              <tbody>
                {boats.map((boat) => (
                  <tr key={boat.id}>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={selectedBoats[boat.id] || false}
                        onChange={() => handleCheckboxChange(boat.id)}
                      />
                    </td>
                    <td>
                      {boat.featured_image ? (
                        <img
                          src={boat.featured_image}
                          alt={boat.description || "Boat"}
                          style={{
                            width: "80px",
                            height: "60px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="bg-light d-flex align-items-center justify-content-center"
                          style={{ width: "80px", height: "60px" }}
                        >
                          No Image
                        </div>
                      )}
                    </td>
                    <td>{boat.description || "Unnamed Boat"}</td>
                    <td>{boat.boatType || "N/A"}</td>
                    <td>{boat.capacity || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </Container>
  );
};

export default FeaturedBoats;
